import React from 'react';

type ProviderEntry = [React.ComponentType<any>, Record<string, any>?];

/**
 * Turns a flat array of `[Provider, props?]` tuples into a single
 * wrapper component that nests them in order — first entry is outermost.
 *
 * Eliminates deep JSX nesting ("provider hell") while producing the
 * exact same React tree at runtime. Zero runtime cost beyond the
 * initial `reduceRight`.
 *
 * @example
 * const AppProviders = composeProviders(
 *   [QueryClientProvider, { client: queryClient }],
 *   [ReduxProvider, { store }],
 *   [ThemeProvider],
 * );
 *
 * <AppProviders>
 *   <App />
 * </AppProviders>
 */
export function composeProviders(...entries: ProviderEntry[]) {
  const ComposedProviders = ({ children }: { children: React.ReactNode }) =>
    entries.reduceRight<React.ReactNode>(
      (acc, [Provider, props]) => <Provider {...props}>{acc}</Provider>,
      children
    ) as React.ReactElement;

  ComposedProviders.displayName = 'ComposedProviders';
  return ComposedProviders;
}
